/**
 * Shared FIFO round-trip trade calculation.
 *
 * Both Leaderboard and Reports use this so numbers always agree.
 */
import { Order } from '../services/AlpacaService';
import { isOptionSymbol } from './symbolUtils';

/* ── Interfaces ── */

export interface SymbolBreakdown {
  symbol: string;
  trades: number;
  pnl: number;
  wins: number;
  losses: number;
}

/** Shape used by the Reports page */
export interface TradeStats {
  totalTrades: number;
  wins: number;
  losses: number;
  winRate: string;
  totalPnl: string;
  avgWin: string;
  avgLoss: string;
  profitFactor: string;
  bestTrade: string;
  worstTrade: string;
  symbolBreakdown: SymbolBreakdown[];
}

/** Shape used by the Leaderboard scorecard */
export interface AggregateStats {
  totalTrades: number;
  winRate: number;
  avgPnlPct: number;
  bestTradePct: number;
  worstTradePct: number;
  profitFactor: number;
  totalPnlDollars: number;
}

export const EMPTY_AGGREGATE: AggregateStats = {
  totalTrades: 0,
  winRate: 0,
  avgPnlPct: 0,
  bestTradePct: 0,
  worstTradePct: 0,
  profitFactor: 0,
  totalPnlDollars: 0,
};

/* ── Core FIFO engine ── */

interface FillEntry {
  price: number;
  qty: number;
}

interface FifoResult {
  wins: number;
  losses: number;
  totalWinAmt: number;
  totalLossAmt: number;
  bestTradeDollars: number;
  worstTradeDollars: number;
  /** Per-round-trip P&L % values (buy-weighted) */
  pnlPcts: number[];
  symbolBreakdown: SymbolBreakdown[];
}

function runFifo(orders: Order[]): FifoResult {
  const filled = orders
    .filter(
      (o) => o.status === 'filled' && o.filled_avg_price && parseFloat(o.filled_qty) > 0 && !isOptionSymbol(o.symbol),
    )
    .sort((a, b) => new Date(a.submitted_at).getTime() - new Date(b.submitted_at).getTime());

  const bySymbol: Record<string, { buys: FillEntry[]; sells: FillEntry[] }> = {};
  for (const o of filled) {
    if (!bySymbol[o.symbol]) bySymbol[o.symbol] = { buys: [], sells: [] };
    const entry: FillEntry = { price: parseFloat(o.filled_avg_price || '0'), qty: parseFloat(o.filled_qty) };
    if (o.side === 'buy') bySymbol[o.symbol].buys.push(entry);
    else bySymbol[o.symbol].sells.push(entry);
  }

  let wins = 0,
    losses = 0,
    totalWinAmt = 0,
    totalLossAmt = 0;
  let bestTrade = -Infinity,
    worstTrade = Infinity;
  const pnlPcts: number[] = [];
  const symbolBreakdown: SymbolBreakdown[] = [];

  for (const [symbol, { buys, sells }] of Object.entries(bySymbol)) {
    let bi = 0,
      si = 0;
    let buyRemain = buys[0]?.qty ?? 0;
    let sellRemain = sells[0]?.qty ?? 0;
    let symPnl = 0,
      symWins = 0,
      symLosses = 0,
      symTrips = 0;

    let tripBuyQty = 0,
      tripBuyCost = 0,
      tripSellQty = 0,
      tripSellRevenue = 0;

    while (bi < buys.length && si < sells.length) {
      const matchQty = Math.min(buyRemain, sellRemain);
      tripBuyQty += matchQty;
      tripBuyCost += matchQty * buys[bi].price;
      tripSellQty += matchQty;
      tripSellRevenue += matchQty * sells[si].price;

      buyRemain -= matchQty;
      sellRemain -= matchQty;

      const buyDone = buyRemain < 0.0001;
      const sellDone = sellRemain < 0.0001;

      if (buyDone) {
        bi++;
        buyRemain = bi < buys.length ? buys[bi].qty : 0;
      }
      if (sellDone) {
        si++;
        sellRemain = si < sells.length ? sells[si].qty : 0;
      }

      if ((buyDone || sellDone) && Math.abs(tripBuyQty - tripSellQty) < 0.0001 && tripBuyQty > 0) {
        const pnl = tripSellRevenue - tripBuyCost;
        const avgBuyPrice = tripBuyCost / tripBuyQty;
        const avgSellPrice = tripSellRevenue / tripSellQty;
        const pnlPct = avgBuyPrice > 0 ? ((avgSellPrice - avgBuyPrice) / avgBuyPrice) * 100 : 0;

        symPnl += pnl;
        symTrips++;
        pnlPcts.push(pnlPct);
        if (pnl > bestTrade) bestTrade = pnl;
        if (pnl < worstTrade) worstTrade = pnl;
        if (pnl > 0) {
          wins++;
          symWins++;
          totalWinAmt += pnl;
        } else {
          losses++;
          symLosses++;
          totalLossAmt += Math.abs(pnl);
        }

        tripBuyQty = 0;
        tripBuyCost = 0;
        tripSellQty = 0;
        tripSellRevenue = 0;
      }
    }

    if (symTrips > 0) {
      symbolBreakdown.push({ symbol, trades: symTrips, pnl: symPnl, wins: symWins, losses: symLosses });
    }
  }

  symbolBreakdown.sort((a, b) => b.pnl - a.pnl);

  return {
    wins,
    losses,
    totalWinAmt,
    totalLossAmt,
    bestTradeDollars: bestTrade === -Infinity ? 0 : bestTrade,
    worstTradeDollars: worstTrade === Infinity ? 0 : worstTrade,
    pnlPcts,
    symbolBreakdown,
  };
}

/* ── Public API ── */

/** Used by Reports — dollar-formatted strings + symbol breakdown */
export function computeRoundTripStats(orders: Order[]): TradeStats {
  const r = runFifo(orders);
  const total = r.wins + r.losses;
  return {
    totalTrades: total,
    wins: r.wins,
    losses: r.losses,
    winRate: total > 0 ? ((r.wins / total) * 100).toFixed(1) : '0',
    totalPnl: (r.totalWinAmt - r.totalLossAmt).toFixed(2),
    avgWin: r.wins > 0 ? (r.totalWinAmt / r.wins).toFixed(2) : '0.00',
    avgLoss: r.losses > 0 ? (r.totalLossAmt / r.losses).toFixed(2) : '0.00',
    profitFactor: r.totalLossAmt > 0 ? (r.totalWinAmt / r.totalLossAmt).toFixed(2) : 'N/A',
    bestTrade: r.bestTradeDollars.toFixed(2),
    worstTrade: r.worstTradeDollars.toFixed(2),
    symbolBreakdown: r.symbolBreakdown,
  };
}

/** Used by Leaderboard scorecard — numeric values + P&L percentages */
export function computeAggregateStats(orders: Order[]): AggregateStats {
  const r = runFifo(orders);
  const total = r.wins + r.losses;
  if (total === 0) return EMPTY_AGGREGATE;

  const bestPct = r.pnlPcts.length > 0 ? Math.max(...r.pnlPcts) : 0;
  const worstPct = r.pnlPcts.length > 0 ? Math.min(...r.pnlPcts) : 0;

  return {
    totalTrades: total,
    winRate: (r.wins / total) * 100,
    avgPnlPct: r.pnlPcts.reduce((a, b) => a + b, 0) / r.pnlPcts.length,
    bestTradePct: bestPct,
    worstTradePct: worstPct,
    profitFactor: r.totalLossAmt > 0 ? r.totalWinAmt / r.totalLossAmt : r.totalWinAmt > 0 ? Infinity : 0,
    totalPnlDollars: r.totalWinAmt - r.totalLossAmt,
  };
}
